import { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Dropdown, DropdownContent, SpinnerLoader } from 'shared/ui'
import { makeTextFieldAutoFocus, makeTextFieldClear, TextField } from 'shared/ui/TextField'
import type { IResponseFilter } from 'entities/Contacts/api/filterTypes'
import { useEventIntegrationListSyncFinished } from 'entities/Integrations/events/EventIntegrationListSyncFinished'
import type { IFilterValue, IWidgetProps } from 'widgets/FilterEditor/types/types'
import { HubspotControl } from 'widgets/FilterEditor/ui/Widget/WidgetHubspot/store/HubspotControl'
import { WidgetHubspotMultiTrigger } from 'widgets/FilterEditor/ui/Widget/WidgetHubspot/ui/WidgetHubspotMulti/WidgetHubspotMultiTrigger'
import { WidgetHubspotMultiDropdownItem } from './WidgetHubspotMultiDropdownItem'
import styles from './styles.module.scss'

export const WidgetHubspotMulti = observer(
  ({ filterItem, expandByDefault = false, parentsDropdownId }: IWidgetProps) => {
    const control = useMemo(() => new HubspotControl(), [])
    const [search, setSearch] = useState('')
    const [isActive, setActive] = useState(false)
    const [showValuesWarning, setShowValuesWarning] = useState(false)
    const filterValuesIds = useMemo(() => {
      const value = filterItem.payload.value

      if (!value) return []

      return (Array.isArray(value) ? value : [value]).map(String)
    }, [filterItem.payload.value])
    const filterConfig = filterItem.config as IResponseFilter<IFilterValue>
    const filterValues = useMemo(() => filterConfig.values ?? [], [filterConfig.values])
    const filteredValues = useMemo(() => {
      const q = search.trim().toLowerCase()

      if (!q) return filterValues

      return filterValues.filter((item) => {
        const text = (item.name ?? item.label ?? '').toLowerCase()
        return text.includes(q)
      })
    }, [filterValues, search])

    const isSearchNoResults = search.trim().length > 0 && !filteredValues.length

    const handleClearAll = () => {
      filterItem.payload.clear()
      control.clearSelection()
      setSearch('')
    }

    const handleToggleItem = useCallback(
      (key: number | string) => {
        control.toggleItem(key)
        filterItem.payload.updateHard({
          value: control.selectedItems.map(({ key }) => String(key)),
        })
      },
      [control, filterItem]
    )

    useEffect(() => {
      setTimeout(() => setActive(expandByDefault), 100)
    }, [expandByDefault])

    useEffect(() => {
      control.setFilterValues(filterValues)
      control.syncActiveItems(filterValuesIds)
    }, [filterValues])

    useEffect(() => {
      if (!isActive && !filterValuesIds.length) return setShowValuesWarning(true)
      if (isActive) return setShowValuesWarning(false)
    }, [isActive, filterValuesIds])

    useEventIntegrationListSyncFinished(({ list }) => {
      filterItem.updateConfigValue(list.list_id, { ...list, count: list.size ?? 0 })
    })

    return (
      <Dropdown
        ariaLabel={`AsyncDropdown_${filterItem.config.name}`}
        placement='bottom-start'
        show={isActive}
        onChangeOpen={setActive}
        className={styles.operatorDropdown}
        parentsDropdownId={parentsDropdownId}
        triggerComponent={() => (
          <WidgetHubspotMultiTrigger
            selectedItems={control.selectedItems}
            showValuesWarning={showValuesWarning}
            loading={control.initialLoading}
          />
        )}
        customComponent={() => (
          <DropdownContent className={styles.dropdownContent}>
            {control.initialLoading ? (
              <SpinnerLoader ariaLabel={'WidgetAsyncTags'} className={styles.spinner} />
            ) : (
              <>
                <div className={styles.asyncDropdownHead}>
                  <TextField
                    variant='integrated'
                    size='small'
                    value={search}
                    onChange={setSearch}
                    InputProps={{ placeholder: 'Search HubSpot lists' }}
                    rightActions={[makeTextFieldClear()]}
                    mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
                  />
                </div>
                <div className={styles.asyncContent}>
                  {filteredValues.map((item) => (
                    <WidgetHubspotMultiDropdownItem
                      item={item}
                      key={item.key}
                      control={control}
                      onChange={handleToggleItem}
                    />
                  ))}
                  {isSearchNoResults && <div className={styles.notFound}>No results found</div>}
                </div>
                {!!filterValuesIds.length && (
                  <div className={styles.asyncDropdownFooter}>
                    <Button
                      text='Clear'
                      contained='secondary'
                      typeBtn='text'
                      onClick={handleClearAll}
                    />
                  </div>
                )}
              </>
            )}
          </DropdownContent>
        )}
      />
    )
  }
)
